import React, { useState } from 'react';
import styled from 'styled-components';
import Tooltip from '../Tooltip';

const StyledIconButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  @media (hover: hover) {
    &:hover {
      opacity: 0.8;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Success state styling */
  &.copied {
    background-color: ${({ theme }: { theme: any }) =>
      theme.background.primary};
    border-radius: 8px;
    padding: 4px;
  }

  /* Optional: Add a subtle animation */
  &.copied img {
    transform: scale(1.1);
    transition: transform 0.2s ease;
  }
`;

interface CopyIconButtonProps {
  textToCopy: string;
  iconUrl: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  style?: React.CSSProperties;
  as?: React.ElementType;
  // Copy feedback options
  showVisualFeedback?: boolean;
  showTooltip?: boolean;
  tooltipText?: string;
  successTooltipText?: string;
  // Callbacks
  onCopySuccess?: () => void;
  onCopyError?: (error: Error) => void;
  [key: string]: any;
}

const CopyIconButton: React.FC<CopyIconButtonProps> = ({
  textToCopy,
  iconUrl,
  onClick = () => {},
  type = 'button',
  disabled = false,
  style = {},
  as,
  // Copy feedback options
  showVisualFeedback = true,
  showTooltip = true,
  tooltipText = 'Copy to clipboard',
  successTooltipText = 'Copied!',
  // Callbacks
  onCopySuccess,
  onCopyError,
  ...props
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    if (isCopied || disabled) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);

      // Call success callback
      onCopySuccess?.();

      // Call the original onClick if provided
      onClick();

      // Reset after 2 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
      onCopyError?.(error as Error);
    }
  };

  const button = (
    <StyledIconButton
      as={as}
      disabled={disabled || isCopied}
      onClick={handleCopy}
      style={style}
      className={isCopied && showVisualFeedback ? 'copied' : ''}
      {...props}
    >
      <img src={iconUrl} alt='copy' />
    </StyledIconButton>
  );

  if (!showTooltip) {
    return button;
  }

  return (
    <Tooltip
      content={isCopied ? successTooltipText : tooltipText}
      show={isCopied}
      delay={200}
    >
      {button}
    </Tooltip>
  );
};

export default CopyIconButton;
